import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { XIcon } from './XIcon';

const FOCUSABLE_SELECTORS = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
const TITLE_ID = 'info-modal-title';

export const InfoModal = ({ isOpen, onClose, title, content, position }) => {
  const modalRef = useRef(null);
  const triggerRef = useRef(null);

InfoModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  content: PropTypes.node.isRequired,
  position: PropTypes.shape({
    top: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    left: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
};

  useEffect(() => {
    if (isOpen) {
      triggerRef.current = document.activeElement;
      setTimeout(() => {
        const focusable = modalRef.current?.querySelectorAll(FOCUSABLE_SELECTORS);
        if (focusable?.length) focusable[0].focus();
      }, 0);
    } else {
      triggerRef.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key === 'Tab') {
        const focusable = [...(modalRef.current?.querySelectorAll(FOCUSABLE_SELECTORS) || [])];
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey) {
          if (document.activeElement === first) {
            event.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const modalContent = (
    <dialog
      ref={modalRef}
      aria-modal="true"
      aria-labelledby={TITLE_ID}
      className="info-modal-popup"
      style={{ top: position.top, left: position.left }}
      open
    >
      <div className="info-modal-header">
        <h3 id={TITLE_ID} className="info-modal-title">{title}</h3>
        <button onClick={onClose} className="info-modal-close-btn" aria-label="Close dialog"><XIcon /></button>
      </div>
      <div className="info-modal-content">{content}</div>
      <div className="info-modal-footer"><button onClick={onClose} className="info-modal-ok-btn">OK</button></div>
    </dialog>
  );

  return createPortal(modalContent, document.body);
};

InfoModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  content: PropTypes.node.isRequired,
  position: PropTypes.shape({
    top: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    left: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
};